"use client";

import { RefObject } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { formatNotificationTime } from "./landing-nav-types";

type Notification = {
    id: string;
    title: string;
    body: string;
    readAt?: Date;
    createdAt: Date | string;
};

type NotificationsPanelProps = {
    isOpen: boolean;
    onToggle: () => void;
    unreadCount: number;
    notifications: Notification[];
    isLoading: boolean;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    removeNotification: (id: string) => void;
    containerRef: RefObject<HTMLDivElement | null>;
    position: "left" | "right";
};

function NotificationList({
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    position,
}: Omit<NotificationsPanelProps, "isOpen" | "onToggle" | "containerRef">) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className={`absolute ${position === "left" ? "left-0" : "right-0"} top-full z-50 mt-3 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]/95 shadow-[0_20px_40px_rgba(4,8,20,0.55)] backdrop-blur`}
        >
            <div className="flex items-center justify-between border-b border-[var(--hopp-panel-strong)] px-4 py-3">
                <h3 className="text-sm font-semibold text-[var(--hopp-text)]">
                    Notifications
                </h3>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={markAllAsRead}
                        className="text-[10px] uppercase tracking-[0.2em] text-[var(--hopp-sapphire)] transition hover:text-[var(--hopp-text)]"
                    >
                        Mark all read
                    </button>
                )}
            </div>

            <div className="max-h-80 overflow-y-auto">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
                        <p className="text-xs text-[var(--hopp-muted)]">
                            Loading...
                        </p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
                        <svg
                            viewBox="0 0 24 24"
                            className="mb-2 h-8 w-8 text-[var(--hopp-muted)]"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <p className="text-xs text-[var(--hopp-muted)]">
                            No notifications yet
                        </p>
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={`group relative border-b border-[var(--hopp-panel-strong)] px-4 py-3 transition last:border-b-0 hover:bg-[var(--hopp-panel-strong)]/50 ${
                                !notification.readAt ? "bg-[var(--hopp-sapphire)]/5" : ""
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${
                                        !notification.readAt
                                            ? "bg-[var(--hopp-sapphire)]"
                                            : "bg-transparent"
                                    }`}
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-[var(--hopp-text)]">
                                        {notification.title}
                                    </p>
                                    <p className="mt-0.5 text-xs text-[var(--hopp-muted)]">
                                        {notification.body}
                                    </p>
                                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--hopp-muted)]">
                                        {formatNotificationTime(notification.createdAt)}
                                    </p>
                                </div>
                                <div className={`flex items-center gap-1 ${position === "right" ? "opacity-0 transition group-hover:opacity-100" : ""}`}>
                                    {!notification.readAt && (
                                        <button
                                            type="button"
                                            onClick={() => markAsRead(notification.id)}
                                            className="flex h-6 w-6 items-center justify-center rounded-full text-[var(--hopp-muted)] transition hover:bg-[var(--hopp-panel-strong)] hover:text-[var(--hopp-text)]"
                                            aria-label="Mark as read"
                                        >
                                            <svg
                                                viewBox="0 0 24 24"
                                                className="h-3.5 w-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M20 6L9 17l-5-5" />
                                            </svg>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeNotification(notification.id)}
                                        className="flex h-6 w-6 items-center justify-center rounded-full text-[var(--hopp-muted)] transition hover:bg-[rgba(255,90,90,0.12)] hover:text-red-400"
                                        aria-label="Remove notification"
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            className="h-3.5 w-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M18 6L6 18M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </motion.div>
    );
}

export function NotificationsPanel({
    isOpen,
    onToggle,
    unreadCount,
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    removeNotification,
    containerRef,
    position,
}: NotificationsPanelProps) {
    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={onToggle}
                className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-muted)] shadow-[0_8px_16px_rgba(3,7,18,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                aria-label="Notifications"
                aria-expanded={isOpen}
            >
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--hopp-sapphire)] text-[10px] font-semibold text-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <NotificationList
                        notifications={notifications}
                        isLoading={isLoading}
                        unreadCount={unreadCount}
                        markAsRead={markAsRead}
                        markAllAsRead={markAllAsRead}
                        removeNotification={removeNotification}
                        position={position}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
